var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ANNOTATION_SHAPE_TYPE, } from '../DataTypes';
import Papa from 'papaparse';
import { Vector3, Color } from 'three';
class AnnotationLoader {
    /**
     * Find column name
     *
     * @param index
     * @param array
     */
    findColumnName(index, array) {
        if (index >= 0 && index < array.length) {
            return array[index];
        }
        else {
            return null;
        }
    }
    /**
     * Find field type and name
     *
     * @param columnName
     */
    findFieldTypeInfo(columnName) {
        const words = columnName.split(' ');
        const lastWord = words.pop();
        const fieldName = words.join(' ');
        let fieldType = '';
        if (lastWord === null || lastWord === void 0 ? void 0 : lastWord.endsWith(')')) {
            const matches = lastWord.match(/\((.*)\)/);
            if (matches) {
                fieldType = matches[1];
            }
        }
        else {
            fieldType = lastWord;
        }
        return {
            name: fieldName.trim(),
            type: fieldType.trim(),
        };
    }
    /**
     * Create url values into object array
     *
     * @param substring
     */
    findURLFieldValues(substring) {
        // Regular expression to remove leading and trailing brackets and split the string into pairs
        const pairs = substring.replace(/^\[\[|\]\]$/g, '').split('],[');
        // Map pairs to objects
        const output = pairs.map((pair) => {
            const [name, url] = pair.split(', ');
            return { name: name.replace('[', ''), url: url.replace(']', '') };
        });
        return output;
    }
    /**
     * Find relevant column indexes by name
     *
     * @param array
     * @param substring
     */
    findColumnIndex(array, substring) {
        const indexes = [];
        for (let i = 0; i < array.length; i++) {
            if (array[i].includes(substring)) {
                indexes.push(i);
            }
        }
        return indexes; // returns array of indexes e.g [0,5]
    }
    /**
     * Calculate the points of annotation
     *
     * @param points
     */
    calculatePoints(points) {
        const pointsArray = JSON.parse(points);
        const convertedPoints = [];
        for (let i = 0; i < pointsArray.length; i++) {
            const innerArray = pointsArray[i];
            convertedPoints.push(new Vector3(innerArray[0], innerArray[1], innerArray[2]));
        }
        return convertedPoints;
    }
    /**
     * Find field values
     *
     * @param values
     * @param columnArray
     * @param columnName
     */
    findFieldValues(values, columnArray, columnName) {
        const fields = this.findColumnIndex(columnArray, columnName).map((columnIndex) => {
            const column = this.findColumnName(columnIndex, columnArray);
            if (column)
                return Object.assign(Object.assign({}, this.findFieldTypeInfo(column)), { value: values[columnIndex] });
        });
        return fields;
        // e.g: fields = [{
        //   name: '',
        //   type: '',
        //   value: '',
        // }]
    }
    /**
     * Validate shape type
     *
     * @param shapeType
     */
    validateShapeType(shapeType) {
        return Object.values(ANNOTATION_SHAPE_TYPE).includes(shapeType);
    }
    /**
     * Validate color
     *
     * @param color
     */
    validateColor(color) {
        // Remove '#' character and convert to lowercase
        const colorCode = color.replace('#', '').toLowerCase();
        const hexString = new Color(color).getHexString().toLowerCase();
        return colorCode === hexString;
    }
    /**
     * Get the list of annotation objects from an CSV input string
     *
     * @param csv        CSV string
     */
    read(csv) {
        return __awaiter(this, void 0, void 0, function* () {
            const annotations = [];
            const parsed = Papa.parse(csv, {
                header: false,
                delimitersToGuess: [','],
            });
            // Get column names (column names are provide column index and names)
            const headerRow = parsed.data[0];
            for (let i = 0; i < parsed.data.length; i++) {
                // First row is skipped. if not column names will be added to values
                if (i == 0)
                    continue;
                const values = Object.values(parsed.data[i]);
                const name = values[this.findColumnIndex(headerRow, 'Name')[0]];
                const color = values[this.findColumnIndex(headerRow, 'Color')[0]];
                const shapeType = values[this.findColumnIndex(headerRow, '3D Shape')[0]];
                const points = values[this.findColumnIndex(headerRow, '3D Points')[0]];
                if (!name ||
                    !this.validateColor(color) ||
                    !this.validateShapeType(shapeType))
                    continue;
                let fields = [];
                if (this.findColumnIndex(headerRow, 'URL').length !== 0) {
                    const urlFields = [];
                    const fieldValues = this.findFieldValues(values, headerRow, 'URL');
                    for (const fieldValue of fieldValues) {
                        const urlField = {
                            name: fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.name,
                            type: fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.type,
                            urls: this.findURLFieldValues(fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.value),
                        };
                        urlFields.push(urlField);
                    }
                    fields = [...fields, ...urlFields];
                }
                if (this.findColumnIndex(headerRow, 'FILE').length !== 0) {
                    const fileFields = [];
                    const fieldValues = this.findFieldValues(values, headerRow, 'FILE');
                    for (const fieldValue of fieldValues) {
                        const fileField = {
                            name: fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.name,
                            type: fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.type,
                            files: this.findURLFieldValues(fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.value),
                        };
                        fileFields.push(fileField);
                    }
                    fields = [...fields, ...fileFields];
                }
                if (this.findColumnIndex(headerRow, 'SELECT').length !== 0) {
                    const selectFields = this.findFieldValues(values, headerRow, 'SELECT');
                    fields = [...fields, ...selectFields];
                }
                if (this.findColumnIndex(headerRow, 'DATE').length !== 0) {
                    const dateFields = this.findFieldValues(values, headerRow, 'DATE');
                    fields = [...fields, ...dateFields];
                }
                if (this.findColumnIndex(headerRow, 'TEXT').length !== 0) {
                    const textFields = this.findFieldValues(values, headerRow, 'TEXT');
                    fields = [...fields, ...textFields];
                }
                if (this.findColumnIndex(headerRow, 'IMAGE').length !== 0) {
                    const imageFields = this.findFieldValues(values, headerRow, 'IMAGE');
                    fields = [...fields, ...imageFields];
                }
                const positions = this.calculatePoints(points);
                annotations.push({
                    name,
                    color,
                    annotation3d: {
                        positions,
                        shapeType,
                    },
                    fields,
                });
            }
            return annotations;
        });
    }
}
export default AnnotationLoader;
